import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { OpportunityCard } from "@/components/OpportunityCard";
import { ModeBadge } from "@/components/Brand";
import { shortMoney } from "@/lib/format";
import { Crosshair, TrendingUp, IndianRupee, Building2, ArrowRight, History, Loader2, Send, CalendarCheck, Trophy } from "lucide-react";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="lf-card p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className={`w-4 h-4 ${accent ? "text-primary" : "text-slate-500"}`} />
      </div>
      <p className="font-display text-2xl font-bold text-slate-50 lf-tabnum">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const currency = profile?.currency || "INR";

  useEffect(() => { api.get("/dashboard").then(({ data }) => setData(data)); }, []);

  if (!data)
    return <div className="h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const top = data.top_opportunities.slice(0, 4);

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-primary mb-1">Welcome back, {user?.name?.split(" ")[0]}</p>
          <h1 className="font-display text-3xl font-bold text-slate-50">Your client-acquisition command center.</h1>
        </div>
        <Button size="lg" className="bg-primary hover:bg-emerald-600 text-white h-12 px-6" data-testid="dashboard-find-clients-btn" onClick={() => navigate("/app/opportunities")}>
          <Crosshair className="w-4 h-4 mr-2" /> Find my best clients
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Building2} label="Analysed" value={data.businesses_analyzed} />
        <Stat icon={TrendingUp} label="High priority" value={data.high_priority} accent />
        <Stat icon={IndianRupee} label="Pipeline value" value={shortMoney(data.pipeline_value, currency)} accent />
        <Stat icon={Send} label="Contacted" value={data.contacted} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 -mt-4">
        <Stat icon={Send} label="Replies" value={data.replies} />
        <Stat icon={CalendarCheck} label="Meetings" value={data.meetings} />
        <Stat icon={Trophy} label="Won revenue" value={shortMoney(data.won_revenue || 0, currency)} accent />
        <Stat icon={History} label="Saved leads" value={data.leads_count} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-semibold text-slate-100 flex items-center gap-3">Your top opportunities <ModeBadge mode={data.mode} testid="dashboard-mode-badge" /></h2>
          <button className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="view-all-opps" onClick={() => navigate("/app/opportunities")}>
            View all 10 <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {top.map((opp, i) => <OpportunityCard key={opp.id} opp={opp} rank={i + 1} currency={currency} onGeneratePitch={(o) => navigate(`/app/opportunity/${o.id}?pitch=1`)} />)}
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2"><History className="w-4 h-4 text-slate-500" /> Recent searches</h2>
        {data.recent_searches.length === 0 ? (
          <p className="text-sm text-slate-500">No searches yet. Click <span className="text-primary">Find my best clients</span> to begin.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {data.recent_searches.map((s) => (
              <button key={s.id} className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-sm text-slate-300 hover:border-slate-700 flex items-center gap-2" data-testid={`recent-search-${s.id}`} onClick={() => navigate("/app/opportunities")}>
                {s.label} <span className="text-[11px] text-slate-500">· {s.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
